import { format } from 'date-fns';
import { ProductClassification, ProductType, ProductOptionsText, SearchParamValues } from '../models/enums';
import { CourseScheduleModel, EnrollmentDetailsModel, SearchProductTileModel } from '../models/globals';

type ProductSchedulingProps = Pick<
    EnrollmentDetailsModel,
    'classification' | 'courseSchedules' | 'isBootCamp' | 'type'
>;

const enrollingNow = (dateNow: Date) => (
    courseSchedule: Pick<CourseScheduleModel, 'enrollmentEndDateUTC'>,
): boolean => {
    return dateNow < new Date(courseSchedule.enrollmentEndDateUTC);
};

const formatAvailableStartDates = (availableSchedules: CourseScheduleModel[]): string => {
    const formattedStartDates = availableSchedules.map(formatStartDate).join(' | ');

    return formatStartDateText(formattedStartDates);
};

const formatStartDate = (courseSchedule: Pick<CourseScheduleModel, 'classStartDateUTC'>): string => {
    return format(new Date(courseSchedule.classStartDateUTC), 'MMMM dd');
};

const formatStartDateText = (formattedStartDates: string): string => {
    // Add more spacing between the word 'Starting' and the rest of the content
    // by encoding NBSP in a string. Refer to E2G-11031 for details.
    // Certain cases might send an empty string, hence the `&&`
    return formattedStartDates && `Starting\xa0\xa0${formattedStartDates}`;
};

const getBgColorClass = (product: Pick<EnrollmentDetailsModel, 'classification'>): string => {
    return {
        [ProductClassification.CareerTraining]: 'bg-career',
        [ProductClassification.Fundamentals]: 'bg-fundamentals',
    }[product.classification];
};

const getColorClass = (product: Pick<EnrollmentDetailsModel, 'classification'>): string => {
    return {
        [ProductClassification.CareerTraining]: 'text-success',
        [ProductClassification.Fundamentals]: 'text-primary',
    }[product.classification];
};

const getCourseTypeSearchParamValue = (product: Pick<EnrollmentDetailsModel, 'classification'>): SearchParamValues => {
    switch (product.classification) {
        case ProductClassification.CareerTraining: {
            return SearchParamValues.CareerTraining;
        }
        case ProductClassification.Fundamentals: {
            return SearchParamValues.Fundamentals;
        }
    }
};

const getDurationText = (
    product: Pick<SearchProductTileModel, 'accessTime' | 'classification' | 'courseDuration' | 'type'>,
    hours = 'Hours',
): string => {
    return (
        [
            !isFundamentalsBundle(product) && product.accessTime,
            product.courseDuration && `${product.courseDuration} Course ${hours}`,
        ]
            .filter(Boolean)
            .join(' / ') || '—'
    );
};

const getDurationTextForBanner = (
    product: Pick<SearchProductTileModel, 'accessTime' | 'classification' | 'courseDuration' | 'type'>,
): string => {
    return getDurationText(product, 'Hrs');
};

const getEnrollmentDateText = (product: ProductSchedulingProps): string => {
    const availableSchedules = getFirstTwoAvailableSchedules(product);

    return formatAvailableStartDates(availableSchedules);
};

// Assume schedules are sorted in ASC order based on classStartDateUTC
const getEnrollmentDateTextForBanner = (product: Pick<EnrollmentDetailsModel, 'courseSchedules'>): string => {
    if (!product.courseSchedules.length) return 'Open Enrollment';

    const dateNow = new Date();
    const currentSchedule = product.courseSchedules.find(enrollingNow(dateNow));

    // ILC's and ILC bundles, with schedules
    if (currentSchedule) {
        const classStart = new Date(currentSchedule.classStartDateUTC);

        if (dateNow < classStart) {
            return formatStartDateText(format(classStart, 'MMMM dd, yyyy'));
        } else {
            return 'Currently Enrolling';
        }
    } else {
        return 'Open Enrollment';
    }
};

const getEnrollmentDateTextForSearchPage = (product: ProductSchedulingProps): string => {
    if (!hasIlcSchedules(product)) return 'Open Enrollment';

    // ILC's and ILC bundles, with schedules
    const dateNow = new Date();
    const availableSchedules = getFirstTwoAvailableSchedules(product, dateNow);

    if (!availableSchedules.length) return 'Open Enrollment';

    const currentClassStart = new Date(availableSchedules[0].classStartDateUTC);

    if (dateNow > currentClassStart) return 'Currently Enrolling';

    return formatAvailableStartDates(availableSchedules);
};

const getFirstAvailableSchedule = (product: ProductSchedulingProps): CourseScheduleModel | undefined => {
    if (!hasIlcSchedules(product)) return;

    const dateNow = new Date();
    return product.courseSchedules.find(enrollingNow(dateNow));
};

const getFirstTwoAvailableSchedules = (
    product: ProductSchedulingProps,
    dateNow = new Date(),
): CourseScheduleModel[] => {
    if (!hasIlcSchedules(product)) return [];

    // ILC's and ILC bundles, with schedules
    return product.courseSchedules.filter(enrollingNow(dateNow)).slice(0, 2);
};

const getOptionText = (
    product: Pick<EnrollmentDetailsModel, 'classification' | 'type'>,
): 'Self-Guided' | 'Instructor-Moderated' | 'Instructor-led' => {
    switch (product.type) {
        case ProductType.Spt:
            return ProductOptionsText.Spt;
        case ProductType.Ctp:
            return ProductOptionsText.Ctp;
        case ProductType.Bundle: {
            if (product.classification == ProductClassification.Fundamentals) return ProductOptionsText.Ilc;
            else return ProductOptionsText.CtpBundle;
        }
        case ProductType.Ilc:
        default:
            return ProductOptionsText.Ilc;
    }
};

const getOptionSubText = (product: ProductSchedulingProps): string => {
    if (hasIlcSchedules(product)) {
        return 'Confirm your start date in cart';
    } else if (product.isBootCamp) {
        return 'Live-Online. Boot Camp';
    } else if (isCtpOrCtpBundle(product)) {
        return 'Self-paced. Study on your own schedule';
    }
    return 'No instructor. Study on your own schedule';
};

const getVoucherOptionText = (
    product: Pick<EnrollmentDetailsModel, 'isVoucherIncluded'>,
): 'Include Voucher: Access to certification exam' | 'No Voucher' => {
    return product.isVoucherIncluded ? 'Include Voucher: Access to certification exam' : 'No Voucher';
};

const hasCtpVoucherOptions = (products: Pick<EnrollmentDetailsModel, 'classification'>[]): boolean => {
    return products.length > 1 && products[0].classification === ProductClassification.CareerTraining;
};

const hasIlcSchedules = (product: ProductSchedulingProps): boolean => {
    return (isFundamentalsBundle(product) || isIlc(product)) && !!product.courseSchedules.length;
};

const isCtpOrCtpBundle = (product: Pick<EnrollmentDetailsModel, 'classification'>): boolean => {
    return product.classification === ProductClassification.CareerTraining;
};

const isFundamentalsBundle = (product: Pick<EnrollmentDetailsModel, 'classification' | 'type'>): boolean => {
    return product.classification === ProductClassification.Fundamentals && product.type === ProductType.Bundle;
};

const isIlc = (product: Pick<EnrollmentDetailsModel, 'classification' | 'type'>): boolean => {
    return product.classification === ProductClassification.Fundamentals && product.type === ProductType.Ilc;
};

const getMarketingCategory = (
    product: Pick<Record<string, unknown>, 'classification' | 'categoryName' | 'subCategoryName' | 'type'>,
): string => {
    return [getMarketingType(product), product.categoryName, product.subCategoryName].join('/');
};

const getMarketingVariant = ({
    classification,
    isVoucherIncluded,
    type,
}: Pick<Record<string, unknown>, 'classification' | 'isVoucherIncluded' | 'type'>): string => {
    switch (type) {
        case ProductType.Bundle: {
            return classification === ProductClassification.CareerTraining
                ? getMarketingVariant({ classification, isVoucherIncluded, type: ProductType.Ctp })
                : getMarketingVariant({ classification, isVoucherIncluded, type: ProductType.Ilc });
        }
        case ProductType.Ctp: {
            return isVoucherIncluded ? 'Voucher' : 'Non-voucher';
        }
        case ProductType.Ilc: {
            return 'Instructor-led';
        }
        case ProductType.Spt: {
            return 'Self-paced';
        }
        /* istanbul ignore next */
        default: {
            throw new Error('missing type value');
        }
    }
};

const getMarketingType = (product: Pick<Record<string, unknown>, 'classification' | 'type'>): string => {
    switch (product.type) {
        case ProductType.Bundle: {
            return product.classification === ProductClassification.CareerTraining
                ? 'Advanced Career Training Bundle'
                : 'Fundamentals Bundle';
        }
        case ProductType.Ctp: {
            return 'Advanced Career Training';
        }
        case ProductType.Ilc: {
            return 'Fundamentals Course';
        }
        case ProductType.Spt: {
            return 'Fundamentals Tutorial';
        }
        /* istanbul ignore next */
        default: {
            throw new Error('missing type value');
        }
    }
};

export {
    getBgColorClass,
    getColorClass,
    getCourseTypeSearchParamValue,
    getDurationText,
    getDurationTextForBanner,
    getFirstAvailableSchedule,
    getEnrollmentDateText,
    getEnrollmentDateTextForBanner,
    getEnrollmentDateTextForSearchPage,
    getMarketingCategory,
    getMarketingVariant,
    getOptionSubText,
    getOptionText,
    getVoucherOptionText,
    hasCtpVoucherOptions,
    isCtpOrCtpBundle,
    isFundamentalsBundle,
};
